'use client';

import { Heading, LayoutContainer, TextLink } from '@petsmart-ui/sparky';

import { useDeviceType } from '@libs/common-next';
import { Accordion } from '@libs/shared-ui/components/Accordion';

import { trackFooterClick } from '../../../analytics';
import type { NavigationColumn } from '../../../types';

import './navigation.scss';

type NavigationProps = Readonly<{
    columns: NavigationColumn[];
}>;

function renderNavigationLinks(links: NavigationColumn['links']) {
    return (
        <ul className="footer-navigation__links">
            {links.map(({ label, url, openInNewTab }) => (
                <li key={`${label}-${url}`}>
                    <TextLink
                        className="footer-navigation__link"
                        variant="secondary"
                        href={url}
                        target={openInNewTab ? '_blank' : undefined}
                        rel={openInNewTab ? 'noopener noreferrer' : undefined}
                        onClick={() => trackFooterClick({ selection: label, link_url: url })}
                    >
                        {label}
                    </TextLink>
                </li>
            ))}
        </ul>
    );
}

export const Navigation = ({ columns }: NavigationProps) => {
    const { isDesktop } = useDeviceType();

    if (!columns.length) return null;

    return (
        <nav aria-label="Footer" className="footer-navigation">
            {isDesktop ? (
                <LayoutContainer className="footer-navigation__desktop">
                    {columns.map(({ id, title, links }) => (
                        <div className="footer-navigation__column" key={id}>
                            <Heading tagName="h3" size="body-lg-bold">
                                {title}
                            </Heading>
                            {renderNavigationLinks(links)}
                        </div>
                    ))}
                </LayoutContainer>
            ) : (
                <Accordion
                    className="footer-navigation__mobile"
                    items={columns.map(({ id, title, links }) => ({
                        id,
                        title,
                        dataTestId: id,
                        children: renderNavigationLinks(links),
                    }))}
                />
            )}
        </nav>
    );
};
